import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { channelUrl } from '../utils/channelUrl';
import { rememberSelectedChannel } from '../utils/selectedChannel';
import { useUser } from '../providers/UserProvider';
import { useChannels } from '../providers/ChannelProvider';
import { ReaderNavbar } from '../components/ReaderNavbar';
import { stripHtml } from '../utils/html';
import { formatDate } from '../utils/formatTime';
import type { ListArticle } from '../types';
import * as listRepository from '../repositories/listRepository';
import * as adminStoryRepository from '../repositories/adminStoryRepository';
import { IconPencil } from '../components/icons';

export default function ListsPage() {
  const { user, logout, isAdmin } = useUser();
  const { refreshChannels } = useChannels();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lists, setLists] = useState<string[]>([]);
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [articles, setArticles] = useState<ListArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [editingList, setEditingList] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [newListName, setNewListName] = useState('');
  const [showScores, setShowScores] = useState(false);
  const [loadingScores, setLoadingScores] = useState(false);
  const [scores, setScores] = useState<Record<string, number>>({});

  const admin = isAdmin;
  const navigate = useNavigate();

  const handleSelectChannel = (channelId: string) => {
    rememberSelectedChannel(channelId);
    navigate(channelUrl(channelId));
  };

  useEffect(() => {
    async function init() {
      const tags = await listRepository.getUserLists();
      setLists(tags);
      const urlList = searchParams.get('list');
      if (urlList && tags.includes(urlList)) {
        setSelectedList(urlList);
      } else if (tags.length > 0) {
        setSelectedList(tags[0]);
      }
      setLoading(false);
    }
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadArticles = useCallback(async (tag: string) => {
    setLoadingArticles(true);
    const arts = await listRepository.getListArticles(tag);
    setArticles(arts);
    setLoadingArticles(false);
  }, []);

  useEffect(() => {
    if (selectedList) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('list', selectedList);
        return next;
      }, { replace: true });
      loadArticles(selectedList);
    }
  }, [selectedList, loadArticles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!admin || !showScores || articles.length === 0) {
      if (!showScores) setScores({});
      setLoadingScores(false);
      return;
    }

    let cancelled = false;
    async function loadScores() {
      setLoadingScores(true);
      try {
        const ids = articles.map((a) => a.article_id);
        const s = await adminStoryRepository.getQualityScoresForArticles(ids);
        if (!cancelled) {
          setScores(s);
        }
      } finally {
        if (!cancelled) {
          setLoadingScores(false);
        }
      }
    }
    loadScores();
    return () => {
      cancelled = true;
    };
  }, [admin, showScores, articles]);

  const handleToggleScores = () => {
    setShowScores((prev) => !prev);
  };

  const handleSetScore = async (articleId: string, score: number, comment?: string) => {
    await adminStoryRepository.upsertQualityScore(articleId, score, comment);
    setScores((prev) => ({ ...prev, [articleId]: score }));
  };

  const handleClearScore = async (articleId: string) => {
    await adminStoryRepository.deleteQualityScore(articleId);
    setScores((prev) => {
      const next = { ...prev };
      delete next[articleId];
      return next;
    });
  };

  const handleCreateList = async () => {
    const tag = newListName.trim();
    if (!tag || tag.length > listRepository.LIST_NAME_MAX_LENGTH) return;
    setNewListName('');
    if (!lists.includes(tag)) {
      await listRepository.createList(tag);
      setLists((prev) => [...prev, tag].sort());
    }
    setSelectedList(tag);
  };

  const handleDeleteList = async (tag: string) => {
    if (!confirm(`Delete list "${tag}" and remove all articles from it?`)) return;
    await listRepository.deleteList(tag);
    setLists((prev) => prev.filter((t) => t !== tag));
    if (selectedList === tag) {
      const remaining = lists.filter((t) => t !== tag);
      setSelectedList(remaining.length > 0 ? remaining[0] : null);
      if (remaining.length === 0) setArticles([]);
    }
  };

  const handleRename = async (oldTag: string) => {
    const newTag = renameValue.trim();
    if (!newTag || newTag === oldTag || newTag.length > listRepository.LIST_NAME_MAX_LENGTH) return;
    await listRepository.renameList(oldTag, newTag);
    setLists((prev) => prev.map((t) => (t === oldTag ? newTag : t)).sort());
    if (selectedList === oldTag) setSelectedList(newTag);
    setEditingList(null);
    setRenameValue('');
  };

  const handleRemoveArticle = async (articleId: string) => {
    if (!selectedList) return;
    await listRepository.removeArticleFromList(articleId, selectedList);
    setArticles((prev) => prev.filter((a) => a.article_id !== articleId));
  };

  return (
    <div className="reader-shell">
      <ReaderNavbar
        selectedChannelId=""
        onSelectChannel={handleSelectChannel}
        user={user}
        onLogout={logout}
      />
      <main className="lists-page">
        <div className="lists-sidebar">
          <h2 className="lists-sidebar-title">Lists</h2>
          <div className="lists-create-row">
            <input
              className="lists-rename-input"
              type="text"
              placeholder="New list..."
              value={newListName}
              maxLength={listRepository.LIST_NAME_MAX_LENGTH}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateList();
                if (e.key === 'Escape') setNewListName('');
              }}
            />
            {newListName.trim() && (
              <button className="lists-rename-save" onClick={handleCreateList}>Add</button>
            )}
          </div>
          {loading ? (
            <div className="lists-empty">Loading...</div>
          ) : lists.length === 0 ? (
            <div className="lists-empty">No lists yet. Create one above, or add articles from the feed.</div>
          ) : (
            <ul className="lists-list">
              {lists.map((tag) => (
                <li key={tag} className={`lists-list-item${selectedList === tag ? ' active' : ''}`}>
                  {editingList === tag ? (
                    <div className="lists-rename-row">
                      <input
                        className="lists-rename-input"
                        value={renameValue}
                        maxLength={listRepository.LIST_NAME_MAX_LENGTH}
                        autoFocus
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(tag);
                          if (e.key === 'Escape') { setEditingList(null); setRenameValue(''); }
                        }}
                      />
                      <button className="lists-rename-save" onClick={() => handleRename(tag)}>Save</button>
                    </div>
                  ) : (
                    <button className="lists-list-btn" onClick={() => setSelectedList(tag)}>
                      {tag}
                    </button>
                  )}
                  <div className="lists-item-actions">
                    <button
                      className="lists-action-btn"
                      title="Rename"
                      onClick={() => { setEditingList(tag); setRenameValue(tag); }}
                    >
                      <IconPencil size={12} />
                    </button>
                    <button
                      className="lists-action-btn lists-action-delete"
                      title="Delete list"
                      onClick={() => handleDeleteList(tag)}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14H7L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="lists-content">
          {admin && selectedList && articles.length > 0 && (
            <div className="lists-toolbar">
              <button
                className={`lists-toggle-scores${showScores ? ' active' : ''}`}
                onClick={handleToggleScores}
                aria-busy={loadingScores}
              >
                {showScores ? 'Hide scores' : 'Show scores'}
              </button>
            </div>
          )}
          {!selectedList ? (
            <div className="lists-empty-content">Select a list to see its articles.</div>
          ) : loadingArticles ? (
            <div className="lists-empty-content">Loading...</div>
          ) : articles.length === 0 ? (
            <div className="lists-empty-content">No articles in this list.</div>
          ) : (
            <table className="lists-article-table">
              <thead>
                <tr>
                  <th>Article</th>
                  <th style={{ width: 60, textAlign: 'center' }}>Tier</th>
                  {showScores && <th style={{ width: 90, textAlign: 'center' }}>Score</th>}
                  <th style={{ width: 120 }}>Domain</th>
                  <th style={{ width: 100 }}>Added</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/read/${a.article_id}`} className="lists-article-link">
                        {a.title || '(Untitled)'}
                      </Link>
                      {a.summary && (
                        <div className="lists-article-summary">{stripHtml(a.summary).slice(0, 120)}</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {a.tier != null && <span className={`tier-badge tier-${a.tier}`}>{a.tier}</span>}
                    </td>
                    {showScores && (
                      <td style={{ textAlign: 'center' }}>
                        <ScoreSelector
                          value={scores[a.article_id] ?? null}
                          onClear={() => handleClearScore(a.article_id)}
                          onSet={(v, comment) => handleSetScore(a.article_id, v, comment)}
                        />
                      </td>
                    )}
                    <td className="lists-article-domain">{a.canonical_domain}</td>
                    <td className="lists-article-date">{formatDate(a.added_at)}</td>
                    <td>
                      <button
                        className="lists-action-btn lists-action-delete"
                        title="Remove from list"
                        onClick={() => handleRemoveArticle(a.article_id)}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

function ScoreSelector({ value, onClear, onSet }: { value: number | null; onClear: () => void; onSet: (score: number, comment?: string) => void }) {
  const [pendingScore, setPendingScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');

  const handleConfirm = () => {
    if (pendingScore != null) {
      onSet(pendingScore, comment.trim() || undefined);
      setPendingScore(null);
      setComment('');
    }
  };

  return (
    <div className="score-selector" style={{ position: 'relative' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          className={`score-btn${value === n ? ' active' : ''}${pendingScore === n ? ' pending' : ''}`}
          onClick={() => {
            if (value === n) { onClear(); setPendingScore(null); setComment(''); }
            else setPendingScore(n);
          }}
          title={value === n ? 'Clear score' : `Score ${n}`}
        >
          {n}
        </button>
      ))}
      {pendingScore != null && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          background: 'var(--panel)',
          border: '1px solid var(--line)',
          borderRadius: 6,
          padding: 6,
          zIndex: 20,
          boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
          minWidth: 160,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
          <input
            type="text"
            placeholder="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); if (e.key === 'Escape') { setPendingScore(null); setComment(''); } }}
            autoFocus
            style={{
              background: 'var(--panel)',
              border: '1px solid var(--line)',
              borderRadius: 4,
              color: 'var(--ink)',
              padding: '3px 6px',
              fontSize: '0.78rem',
            }}
          />
          <button
            onClick={handleConfirm}
            style={{
              background: 'var(--green-600)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '3px 8px',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}
